import createRequest from '../../../shared/api/portalApiClient';
import { env } from '../../../config/env';

export type MembershipTypeRow = {
  membershipTypeUuid: string;
  storeUuid?: string;
  name: string;
  description?: string;
  tierLevel: number;
  themeColorToken?: string;
  pricePaise: number;
  monthlyPricePaise?: number;
  annualPricePaise?: number;
  validityDays?: number;
  unlockCriteria?: string;
  perksJson?: string;
  // Benefit engine (redesign): POINTS | DISCOUNT | VISITS | PERKS_ONLY.
  benefitType?: string;
  discountPercent?: number;
  discountFlatPaise?: number;
  includedVisits?: number;
  pointsEnabled?: boolean;
  pointsEarnRatePaisePerPoint?: number;
  pointsRedeemPaisePerPoint?: number;
  pointsMinRedeem?: number;
  pointsExpiryDays?: number;
  isActive: boolean;
};

export type MembershipRequestRow = {
  requestUuid: string;
  customerName: string;
  customerPhoneMasked?: string | null;
  planName: string;
  tierLevel?: number | null;
  themeColorToken?: string | null;
  pricePaise?: number | null;
  status: string;
  createdAt?: string | null;
  expiresAt?: string | null;
};

export type MembershipAnalytics = {
  activeCount: number;
  revenuePaise: number;
  pointsLiabilityPaise: number;
  byTier: Array<{
    membershipTypeUuid: string;
    name: string;
    activeCount: number;
    revenuePaise: number;
    pointsLiabilityPaise: number;
  }>;
};

/**
 * Stage 1 KPI snapshot. Every metric is nullable — the UI renders `—` when
 * the value is absent rather than fabricating a zero. Mirrors the backend
 * `MembershipKpiResponse`.
 */
export type MembershipTierKpiRow = {
  membershipTypeUuid: string;
  name: string;
  totalMembers: number | null;
  tierRevenue30dPaise: number | null;
  avgMemberLtvPaise: number | null;
  renewalRate30d: number | null;
  pointsLiabilityPaise: number | null;
};

export type MembershipDraftPayload = Record<string, unknown>;

export type MembershipDraftSnapshot = {
  draftUuid: string;
  stepReached: number;
  payload: MembershipDraftPayload;
  updatedAt: string | null;
  expiresAt: string | null;
};

export type MemberSummary = {
  membershipUuid: string;
  userUuid: string | null;
  userName: string | null;
  phone: string | null;
  tierName: string | null;
  tierLevel: number | null;
  themeColorToken: string | null;
  status: string | null;
  startsAt: string | null;
  expiresAt: string | null;
  lifetimeSpendPaise: number | null;
  pointsBalance: number | null;
  lastVisitAt: string | null;
  recentVisits: Array<{
    queueUuid: string;
    completedAt: string | null;
  }>;
};

export type MembershipKpiSnapshot = {
  totalMembers: number | null;
  tierRevenue30dPaise: number | null;
  avgMemberLtvPaise: number | null;
  retentionRate30d: number | null;
  pointsLiabilityPaise: number | null;
  byTier: MembershipTierKpiRow[];
  windowStart: string | null;
  windowEnd: string | null;
  cachedAtEpochMillis: number | null;
};

export type MembershipTypeNameAvailability = {
  name: string;
  available: boolean;
};

export type MembershipMemberRow = {
  membershipUuid: string;
  membershipTypeUuid: string;
  membershipTypeName: string;
  tierLevel: number;
  /** Real users.uuid when claimed; pending_customer_uuid when pending. */
  userUuid: string;
  userName: string;
  phone: string;
  email: string;
  status: string;
  startsAt: string;
  issuedAt: string;
  expiresAt: string;
  pointsBalance: number;
  // Visit tracking: general footfall + (for visit packs) pack usage.
  totalVisits?: number | null;
  lastVisitedAt?: string | null;
  benefitType?: string | null;
  includedVisits?: number | null;
  visitsUsed?: number | null;
  visitsRemaining?: number | null;
  /**
   * `true` when the customer signed up on mobile and the membership is
   * linked to a real users row. `false` when this is still a pending
   * walk-in (issued by the merchant; the customer hasn't signed up yet).
   * Undefined for backwards compatibility with cached responses that
   * predate the field.
   */
  customerClaimed?: boolean;
};

export type UserLookupRow = {
  userUuid: string;
  name: string;
  phone: string;
  email: string;
};

export type MembershipWalkInIssueInput = {
  phone: string;
  name: string;
  email?: string;
  typeUuid: string;
  startsAt?: string;
  validityOverrideDays?: number;
  purchaseAmountPaise?: number;
  note?: string;
};

export type MembershipWalkInCustomer = {
  userUuid: string;
  name: string;
  phone: string;
  email: string;
  status: string;
  authProvider: string;
};

export type MembershipWalkInIssueResponse = {
  membership: MembershipDetail;
  customer: MembershipWalkInCustomer;
  createdCustomer: boolean;
};

export type MembershipDetail = {
  membershipUuid: string;
  storeUuid: string;
  userUuid: string;
  membershipTypeUuid: string;
  tierLevelSnapshot: number;
  nameSnapshot: string;
  themeColorTokenSnapshot: string;
  startsAt: string;
  issuedAt: string;
  expiresAt: string;
  status: string;
  revokedReason: string;
  revokedAt: string;
  issuedByAdminUuid: string;
  purchaseAmountPaise: number;
  pointsBalance: number;
  createdAt: string;
  updatedAt: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  benefitType?: string;
  includedVisits?: number | null;
  visitsUsed?: number | null;
  visitsRemaining?: number | null;
};

export type MembershipAuditEventRow = {
  membershipUuid: string;
  userUuid: string;
  customerName: string;
  tierName: string;
  action: string;
  actorType: string;
  createdAt: string;
};

export type ExpiringMemberRow = {
  membershipUuid: string;
  userUuid: string;
  customerName: string;
  pointsAtRisk: number;
  expiresAt: string;
};

export type UpgradeCandidateRow = {
  membershipUuid: string;
  userUuid: string;
  customerName: string;
  currentTierName: string;
  nextTierName: string;
  currentTierLevel: number;
  progressValue: number;
  progressUnit: string;
  progressPercent: number;
};

export type MembershipLedgerEntry = {
  ledgerUuid: string;
  storeUuid: string;
  userUuid: string;
  membershipUuid: string;
  eventType: string;
  pointsDelta: number;
  balanceAfter: number;
  sourceRef: string;
  idempotencyKey: string;
  spendAmountPaise: number;
  redeemValuePaise: number;
  actorAdminUuid: string;
  note: string;
  createdAt: string;
};

export type Paginated<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
};

export type MembershipIssueInput = {
  userUuid: string;
  typeUuid: string;
  startsAt?: string;
  validityOverrideDays?: number;
  purchaseAmountPaise?: number;
  note?: string;
};

export type MembershipPointsAdjustInput = {
  membershipUuid: string;
  deltaPoints: number;
  reason: string;
  idempotencyKey?: string;
};

export type MembershipPointsEarnInput = {
  membershipUuid: string;
  spendAmountPaise: number;
  sourceRef?: string;
  idempotencyKey: string;
};

export type MembershipPointsRedeemInput = {
  membershipUuid: string;
  points: number;
  sourceRef?: string;
  idempotencyKey: string;
};

export type MembershipBroadcastInput = {
  audience: string;
  message: string;
};

export type MembershipBroadcastResponse = {
  queued: number;
};

type ApiEnvelope<T> = {
  data?: T;
};

const request = createRequest(env.apiBase || '');

const requiredSourceRef = (sourceRef: string | undefined, idempotencyKey: string): string => {
  const trimmed = String(sourceRef || '').trim();
  return trimmed || idempotencyKey;
};

const unwrap = <T,>(payload: ApiEnvelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data as T;
  }
  return payload as T;
};

const asNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asBool = (value: unknown): boolean => Boolean(value);
const asText = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const mapType = (raw: any): MembershipTypeRow => ({
  membershipTypeUuid: asText(raw?.membershipTypeUuid),
  storeUuid: asText(raw?.storeUuid),
  name: asText(raw?.name),
  description: asText(raw?.description),
  tierLevel: asNumber(raw?.tierLevel),
  themeColorToken: asText(raw?.themeColorToken),
  pricePaise: asNumber(raw?.pricePaise),
  monthlyPricePaise: asNumber(raw?.monthlyPricePaise ?? raw?.pricePaise),
  annualPricePaise: raw?.annualPricePaise == null ? undefined : asNumber(raw?.annualPricePaise),
  validityDays: asNumber(raw?.validityDays),
  unlockCriteria: asText(raw?.unlockCriteria),
  perksJson: asText(raw?.perksJson),
  benefitType: asText(raw?.benefitType) || undefined,
  discountPercent: raw?.discountPercent == null ? undefined : asNumber(raw?.discountPercent),
  discountFlatPaise: raw?.discountFlatPaise == null ? undefined : asNumber(raw?.discountFlatPaise),
  includedVisits: raw?.includedVisits == null ? undefined : asNumber(raw?.includedVisits),
  pointsEnabled: asBool(raw?.pointsEnabled),
  pointsEarnRatePaisePerPoint: asNumber(raw?.pointsEarnRatePaisePerPoint),
  pointsRedeemPaisePerPoint: asNumber(raw?.pointsRedeemPaisePerPoint),
  pointsMinRedeem: asNumber(raw?.pointsMinRedeem),
  pointsExpiryDays: asNumber(raw?.pointsExpiryDays),
  isActive: asBool(raw?.isActive)
});

export type MembershipTypeUpsertRequest = {
  name: string;
  description: string;
  tierLevel?: number;
  themeColorToken: string;
  pricePaise: number;
  monthlyPricePaise?: number;
  annualPricePaise?: number;
  validityDays: number;
  unlockCriteria: string;
  perksJson: string;
  // Benefit engine (redesign): POINTS | DISCOUNT | VISITS | PERKS_ONLY.
  benefitType?: string;
  discountPercent?: number;
  discountFlatPaise?: number;
  includedVisits?: number;
  visitsServiceUuid?: string;
  pointsEnabled: boolean;
  pointsEarnRatePaisePerPoint: number;
  pointsRedeemPaisePerPoint: number;
  pointsMinRedeem: number;
  pointsExpiryDays: number;
  isActive?: boolean;
};

const mapAnalytics = (raw: any): MembershipAnalytics => ({
  activeCount: asNumber(raw?.activeCount),
  revenuePaise: asNumber(raw?.revenuePaise),
  pointsLiabilityPaise: asNumber(raw?.pointsLiabilityPaise),
  byTier: Array.isArray(raw?.byTier)
    ? raw.byTier.map((item: any) => ({
      membershipTypeUuid: asText(item?.membershipTypeUuid),
      name: asText(item?.name),
      activeCount: asNumber(item?.activeCount),
      revenuePaise: asNumber(item?.revenuePaise),
      pointsLiabilityPaise: asNumber(item?.pointsLiabilityPaise)
    }))
    : []
});

const mapMember = (raw: any): MembershipMemberRow => ({
  membershipUuid: asText(raw?.membershipUuid),
  membershipTypeUuid: asText(raw?.membershipTypeUuid),
  membershipTypeName: asText(raw?.membershipTypeName),
  tierLevel: asNumber(raw?.tierLevel),
  userUuid: asText(raw?.userUuid),
  userName: asText(raw?.userName),
  phone: asText(raw?.phone),
  email: asText(raw?.email),
  status: asText(raw?.status),
  startsAt: asText(raw?.startsAt),
  issuedAt: asText(raw?.issuedAt),
  expiresAt: asText(raw?.expiresAt),
  pointsBalance: asNumber(raw?.pointsBalance),
  totalVisits: raw?.totalVisits == null ? null : asNumber(raw?.totalVisits),
  lastVisitedAt: asText(raw?.lastVisitedAt) || null,
  benefitType: asText(raw?.benefitType) || null,
  includedVisits: raw?.includedVisits == null ? null : asNumber(raw?.includedVisits),
  visitsUsed: raw?.visitsUsed == null ? null : asNumber(raw?.visitsUsed),
  visitsRemaining: raw?.visitsRemaining == null ? null : asNumber(raw?.visitsRemaining),
  customerClaimed: typeof raw?.customerClaimed === 'boolean' ? raw.customerClaimed : undefined
});

const mapMembershipDetail = (raw: any): MembershipDetail => ({
  membershipUuid: asText(raw?.membershipUuid),
  storeUuid: asText(raw?.storeUuid),
  userUuid: asText(raw?.userUuid),
  membershipTypeUuid: asText(raw?.membershipTypeUuid),
  tierLevelSnapshot: asNumber(raw?.tierLevelSnapshot),
  nameSnapshot: asText(raw?.nameSnapshot),
  themeColorTokenSnapshot: asText(raw?.themeColorTokenSnapshot),
  startsAt: asText(raw?.startsAt),
  issuedAt: asText(raw?.issuedAt),
  expiresAt: asText(raw?.expiresAt),
  status: asText(raw?.status),
  revokedReason: asText(raw?.revokedReason),
  revokedAt: asText(raw?.revokedAt),
  issuedByAdminUuid: asText(raw?.issuedByAdminUuid),
  purchaseAmountPaise: asNumber(raw?.purchaseAmountPaise),
  pointsBalance: asNumber(raw?.pointsBalance),
  createdAt: asText(raw?.createdAt),
  updatedAt: asText(raw?.updatedAt),
  customerName: raw?.customerName != null ? asText(raw.customerName) : undefined,
  customerPhone: raw?.customerPhone != null ? asText(raw.customerPhone) : undefined,
  customerEmail: raw?.customerEmail != null ? asText(raw.customerEmail) : undefined,
  benefitType: raw?.benefitType != null ? asText(raw.benefitType) : undefined,
  includedVisits: raw?.includedVisits == null ? null : asNumber(raw.includedVisits),
  visitsUsed: raw?.visitsUsed == null ? null : asNumber(raw.visitsUsed),
  visitsRemaining: raw?.visitsRemaining == null ? null : asNumber(raw.visitsRemaining)
});

const mapLedgerEntry = (raw: any): MembershipLedgerEntry => ({
  ledgerUuid: asText(raw?.ledgerUuid),
  storeUuid: asText(raw?.storeUuid),
  userUuid: asText(raw?.userUuid),
  membershipUuid: asText(raw?.membershipUuid),
  eventType: asText(raw?.eventType),
  pointsDelta: asNumber(raw?.pointsDelta),
  balanceAfter: asNumber(raw?.balanceAfter),
  sourceRef: asText(raw?.sourceRef),
  idempotencyKey: asText(raw?.idempotencyKey),
  spendAmountPaise: asNumber(raw?.spendAmountPaise),
  redeemValuePaise: asNumber(raw?.redeemValuePaise),
  actorAdminUuid: asText(raw?.actorAdminUuid),
  note: asText(raw?.note),
  createdAt: asText(raw?.createdAt)
});

const asNullableNumber = (value: unknown): number | null => {
  if (value == null) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapTierKpiRow = (raw: any): MembershipTierKpiRow => ({
  membershipTypeUuid: asText(raw?.membershipTypeUuid),
  name: asText(raw?.name),
  totalMembers: asNullableNumber(raw?.totalMembers),
  tierRevenue30dPaise: asNullableNumber(raw?.tierRevenue30dPaise),
  avgMemberLtvPaise: asNullableNumber(raw?.avgMemberLtvPaise),
  renewalRate30d: asNullableNumber(raw?.renewalRate30d),
  pointsLiabilityPaise: asNullableNumber(raw?.pointsLiabilityPaise),
});

export const membershipApi = {
  async listPendingRequests(): Promise<MembershipRequestRow[]> {
    const payload = await request<any>('/v1/admin/memberships/requests?status=PENDING', {
      method: 'GET'
    });
    const data = unwrap<any>(payload);
    return Array.isArray(data)
      ? data.map((r: any) => ({
          requestUuid: asText(r?.requestUuid),
          customerName: asText(r?.customerName),
          customerPhoneMasked: r?.customerPhoneMasked ?? null,
          planName: asText(r?.planName),
          tierLevel: asNullableNumber(r?.tierLevel),
          themeColorToken: r?.themeColorToken ?? null,
          pricePaise: asNullableNumber(r?.pricePaise),
          status: asText(r?.status),
          createdAt: r?.createdAt ?? null,
          expiresAt: r?.expiresAt ?? null,
        }))
      : [];
  },

  async activateRequest(requestUuid: string, code: string): Promise<void> {
    await request<any>(`/v1/admin/memberships/requests/${encodeURIComponent(requestUuid)}/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  },

  async getTypes(): Promise<Paginated<MembershipTypeRow>> {
    const payload = await request<any>('/v1/admin/memberships/types?page=0&pageSize=100', {
      method: 'GET'
    });
    const data = unwrap<any>(payload);
    const items = Array.isArray(data?.items) ? data.items.map(mapType) : [];
    return {
      items,
      page: asNumber(data?.page),
      pageSize: asNumber(data?.pageSize),
      totalElements: asNumber(data?.totalElements),
      totalPages: asNumber(data?.totalPages),
      hasNext: asBool(data?.hasNext)
    };
  },

  async getKpis(): Promise<MembershipKpiSnapshot> {
    const payload = await request<any>('/v1/admin/memberships/kpis?window=30d', {
      method: 'GET'
    });
    const data = unwrap<any>(payload);
    return {
      totalMembers: asNullableNumber(data?.totalMembers),
      tierRevenue30dPaise: asNullableNumber(data?.tierRevenue30dPaise),
      avgMemberLtvPaise: asNullableNumber(data?.avgMemberLtvPaise),
      retentionRate30d: asNullableNumber(data?.retentionRate30d),
      pointsLiabilityPaise: asNullableNumber(data?.pointsLiabilityPaise),
      byTier: Array.isArray(data?.byTier) ? data.byTier.map(mapTierKpiRow) : [],
      windowStart: data?.windowStart ? asText(data.windowStart) : null,
      windowEnd: data?.windowEnd ? asText(data.windowEnd) : null,
      cachedAtEpochMillis: asNullableNumber(data?.cachedAtEpochMillis),
    };
  },

  async getTypesWithStats(): Promise<Paginated<MembershipTypeRow & { stats?: MembershipTierKpiRow | null }>> {
    const payload = await request<any>(
      '/v1/admin/memberships/types?page=0&pageSize=100&includeStats=true',
      { method: 'GET' }
    );
    const data = unwrap<any>(payload);
    const items = Array.isArray(data?.items)
      ? data.items.map((raw: any) => ({
          ...mapType(raw),
          stats: raw?.stats ? mapTierKpiRow(raw.stats) : null,
        }))
      : [];
    return {
      items,
      page: asNumber(data?.page),
      pageSize: asNumber(data?.pageSize),
      totalElements: asNumber(data?.totalElements),
      totalPages: asNumber(data?.totalPages),
      hasNext: asBool(data?.hasNext),
    };
  },

  async getAnalytics(): Promise<MembershipAnalytics> {
    const payload = await request<any>('/v1/admin/memberships/analytics/summary', {
      method: 'GET'
    });
    return mapAnalytics(unwrap<any>(payload));
  },

  async checkTypeNameAvailability(name: string, excludeTypeUuid?: string): Promise<MembershipTypeNameAvailability> {
    const params = new URLSearchParams({ name: String(name || '') });
    if (excludeTypeUuid) {
      params.set('excludeTypeUuid', excludeTypeUuid);
    }
    const payload = await request<any>(`/v1/admin/memberships/types/name-availability?${params.toString()}`, {
      method: 'GET'
    });
    const data = unwrap<any>(payload);
    return {
      name: asText(data?.name),
      available: asBool(data?.available)
    };
  },

  async getType(typeUuid: string): Promise<MembershipTypeRow> {
    const payload = await request<any>(`/v1/admin/memberships/types/${encodeURIComponent(typeUuid)}`, {
      method: 'GET'
    });
    return mapType(unwrap<any>(payload));
  },

  async getMembersByType(params: {
    typeUuid?: string;
    status?: string;
    query?: string;
    page?: number;
    pageSize?: number;
  }): Promise<Paginated<MembershipMemberRow>> {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.query) search.set('query', params.query);
    search.set('page', String(params.page ?? 0));
    search.set('pageSize', String(params.pageSize ?? 20));
    const path = params.typeUuid
      ? `/v1/admin/memberships/types/${encodeURIComponent(params.typeUuid)}/members?${search.toString()}`
      : `/v1/admin/memberships/members?${search.toString()}`;
    const payload = await request<any>(
      path,
      { method: 'GET' }
    );
    const data = unwrap<any>(payload);
    return {
      items: Array.isArray(data?.items) ? data.items.map(mapMember) : [],
      page: asNumber(data?.page),
      pageSize: asNumber(data?.pageSize),
      totalElements: asNumber(data?.totalElements),
      totalPages: asNumber(data?.totalPages),
      hasNext: asBool(data?.hasNext)
    };
  },

  async getMembershipDraft(): Promise<MembershipDraftSnapshot | null> {
    const payload = await request<any>('/v1/admin/memberships/drafts/membership', {
      method: 'GET',
    });
    const data = unwrap<any>(payload);
    if (!data) return null;
    return {
      draftUuid: asText(data?.draftUuid),
      stepReached: asNumber(data?.stepReached) || 1,
      payload: (data?.payload && typeof data.payload === 'object') ? data.payload : {},
      updatedAt: data?.updatedAt ? asText(data.updatedAt) : null,
      expiresAt: data?.expiresAt ? asText(data.expiresAt) : null,
    };
  },

  async saveMembershipDraft(input: {
    stepReached: number;
    payload: MembershipDraftPayload;
  }): Promise<MembershipDraftSnapshot> {
    const payload = await request<any>('/v1/admin/memberships/drafts/membership', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = unwrap<any>(payload);
    return {
      draftUuid: asText(data?.draftUuid),
      stepReached: asNumber(data?.stepReached) || input.stepReached,
      payload: (data?.payload && typeof data.payload === 'object') ? data.payload : input.payload,
      updatedAt: data?.updatedAt ? asText(data.updatedAt) : null,
      expiresAt: data?.expiresAt ? asText(data.expiresAt) : null,
    };
  },

  async discardMembershipDraft(): Promise<void> {
    await request<any>('/v1/admin/memberships/drafts/membership', {
      method: 'DELETE',
    });
  },

  async issueMembershipByPhone(input: {
    phone: string;
    tierUuid: string;
    startsAt?: string;
  }): Promise<{
    membership: MembershipDetail;
    customerCreated: boolean;
    /**
     * Ready-to-render row from the backend (item #18). When non-null the
     * caller can splice it directly into the members-list query cache;
     * when null the caller should fall back to invalidating the list.
     */
    memberRow: MembershipMemberRow | null;
  }> {
    const payload = await request<ApiEnvelope<unknown>>('/v1/admin/memberships/issue-by-phone', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    });
    const data = unwrap<any>(payload);
    return {
      membership: mapMembershipDetail(data?.membership),
      customerCreated: asBool(data?.customerCreated),
      memberRow: data?.memberRow ? mapMember(data.memberRow) : null,
    };
  },

  async getMemberSummary(membershipUuid: string): Promise<MemberSummary> {
    const payload = await request<any>(
      `/v1/admin/memberships/members/${encodeURIComponent(membershipUuid)}/summary`,
      { method: 'GET' }
    );
    const data = unwrap<any>(payload);
    return {
      membershipUuid: asText(data?.membershipUuid),
      userUuid: data?.userUuid ? asText(data.userUuid) : null,
      userName: data?.userName ? asText(data.userName) : null,
      phone: data?.phone ? asText(data.phone) : null,
      tierName: data?.tierName ? asText(data.tierName) : null,
      tierLevel: asNullableNumber(data?.tierLevel),
      themeColorToken: data?.themeColorToken ? asText(data.themeColorToken) : null,
      status: data?.status ? asText(data.status) : null,
      startsAt: data?.startsAt ? asText(data.startsAt) : null,
      expiresAt: data?.expiresAt ? asText(data.expiresAt) : null,
      lifetimeSpendPaise: asNullableNumber(data?.lifetimeSpendPaise),
      pointsBalance: asNullableNumber(data?.pointsBalance),
      lastVisitAt: data?.lastVisitAt ? asText(data.lastVisitAt) : null,
      recentVisits: Array.isArray(data?.recentVisits)
        ? data.recentVisits.map((visit: any) => ({
          queueUuid: asText(visit?.queueUuid),
          completedAt: visit?.completedAt ? asText(visit.completedAt) : null,
        }))
        : [],
    };
  },

  async issueMembership(input: MembershipIssueInput): Promise<MembershipDetail> {
    const payload = await request<ApiEnvelope<unknown>>('/v1/admin/memberships/issue', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    });
    return mapMembershipDetail(unwrap<unknown>(payload));
  },

  async issueWalkInMembership(input: MembershipWalkInIssueInput): Promise<MembershipWalkInIssueResponse> {
    const payload = await request<ApiEnvelope<unknown>>('/v1/admin/memberships/issue-walk-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    });
    const data = unwrap<any>(payload);
    return {
      membership: mapMembershipDetail(data?.membership),
      customer: {
        userUuid: asText(data?.customer?.userUuid),
        name: asText(data?.customer?.name),
        phone: asText(data?.customer?.phone),
        email: asText(data?.customer?.email),
        status: asText(data?.customer?.status),
        authProvider: asText(data?.customer?.authProvider)
      },
      createdCustomer: asBool(data?.createdCustomer)
    };
  },

  async revokeMembership(membershipUuid: string, reason: string): Promise<MembershipDetail> {
    const payload = await request<ApiEnvelope<unknown>>(`/v1/admin/memberships/${encodeURIComponent(membershipUuid)}/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason })
    });
    return mapMembershipDetail(unwrap<unknown>(payload));
  },

  async renewMembership(membershipUuid: string, validityDaysOverride?: number): Promise<MembershipDetail> {
    const payload = await request<ApiEnvelope<unknown>>(`/v1/admin/memberships/${encodeURIComponent(membershipUuid)}/renew`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ validityDaysOverride })
    });
    return mapMembershipDetail(unwrap<unknown>(payload));
  },

  async searchUsersByPhone(query: string): Promise<UserLookupRow[]> {
    const trimmedQuery = String(query || '').trim();
    const digitsOnly = trimmedQuery.replace(/\D/g, '');
    const normalizedPhone = digitsOnly.length === 12 && digitsOnly.startsWith('91')
      ? digitsOnly.slice(2)
      : digitsOnly;
    const isPhoneLikeQuery = /^[\d\s()+-]+$/.test(trimmedQuery);
    const isExactPhoneLookup = /^\d{10}$/.test(normalizedPhone);
    if (isPhoneLikeQuery && !isExactPhoneLookup) {
      return [];
    }
    const endpoint = isExactPhoneLookup
      ? `/v1/store/users/lookup-by-phone?${new URLSearchParams({ phone: normalizedPhone }).toString()}`
      : `/v1/store/users/search?${new URLSearchParams({ query: trimmedQuery, limit: '20' }).toString()}`;
    const payload = await request<any>(endpoint, {
      method: 'GET'
    });
    const rows = unwrap<any>(payload);
    const content = Array.isArray(rows) ? rows : [];
    return content
      .map((row: any) => ({
        userUuid: asText(row?.userUuid || row?.uuid),
        name: asText(row?.name || `${asText(row?.firstname)} ${asText(row?.lastname)}`.trim()),
        phone: asText(row?.phone),
        email: asText(row?.email)
      }))
      .slice(0, 20);
  },

  async getMembership(membershipUuid: string): Promise<MembershipDetail> {
    const payload = await request<any>(`/v1/admin/memberships/${encodeURIComponent(membershipUuid)}`, {
      method: 'GET'
    });
    return mapMembershipDetail(unwrap<any>(payload));
  },

  async getMembershipLedger(params: {
    membershipUuid: string;
    eventType?: string;
    fromInstant?: string;
    toInstant?: string;
    page?: number;
    pageSize?: number;
  }): Promise<Paginated<MembershipLedgerEntry>> {
    const search = new URLSearchParams();
    if (params.eventType) search.set('eventType', params.eventType);
    if (params.fromInstant) search.set('fromInstant', params.fromInstant);
    if (params.toInstant) search.set('toInstant', params.toInstant);
    search.set('page', String(params.page ?? 0));
    search.set('pageSize', String(params.pageSize ?? 20));
    const payload = await request<any>(
      `/v1/admin/memberships/${encodeURIComponent(params.membershipUuid)}/points/ledger?${search.toString()}`,
      { method: 'GET' }
    );
    const data = unwrap<any>(payload);
    return {
      items: Array.isArray(data?.items) ? data.items.map(mapLedgerEntry) : [],
      page: asNumber(data?.page),
      pageSize: asNumber(data?.pageSize),
      totalElements: asNumber(data?.totalElements),
      totalPages: asNumber(data?.totalPages),
      hasNext: asBool(data?.hasNext)
    };
  },

  async adjustPoints(params: MembershipPointsAdjustInput): Promise<MembershipLedgerEntry> {
    const payload = await request<any>(`/v1/admin/memberships/${encodeURIComponent(params.membershipUuid)}/points/adjust`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deltaPoints: params.deltaPoints,
        reason: params.reason,
        idempotencyKey: params.idempotencyKey
      })
    });
    return mapLedgerEntry(unwrap<any>(payload));
  },

  async createType(input: MembershipTypeUpsertRequest): Promise<MembershipTypeRow> {
    const payload = await request<any>('/v1/admin/memberships/types', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    });
    return mapType(unwrap<any>(payload));
  },

  async updateType(typeUuid: string, input: MembershipTypeUpsertRequest): Promise<MembershipTypeRow> {
    const payload = await request<any>(`/v1/admin/memberships/types/${encodeURIComponent(typeUuid)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    });
    return mapType(unwrap<any>(payload));
  },

  async activateType(typeUuid: string): Promise<MembershipTypeRow> {
    const payload = await request<any>(`/v1/admin/memberships/types/${encodeURIComponent(typeUuid)}/activate`, {
      method: 'POST'
    });
    return mapType(unwrap<any>(payload));
  },

  async deactivateType(typeUuid: string): Promise<MembershipTypeRow> {
    const payload = await request<any>(`/v1/admin/memberships/types/${encodeURIComponent(typeUuid)}/deactivate`, {
      method: 'POST'
    });
    return mapType(unwrap<any>(payload));
  },

  async deleteType(typeUuid: string): Promise<void> {
    await request<any>(`/v1/admin/memberships/types/${encodeURIComponent(typeUuid)}`, {
      method: 'DELETE'
    });
  },

  async earnPoints(params: MembershipPointsEarnInput): Promise<MembershipLedgerEntry> {
    const payload = await request<any>(`/v1/admin/memberships/${encodeURIComponent(params.membershipUuid)}/points/earn`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        spendAmountPaise: params.spendAmountPaise,
        sourceRef: requiredSourceRef(params.sourceRef, params.idempotencyKey),
        idempotencyKey: params.idempotencyKey
      })
    });
    return mapLedgerEntry(unwrap<any>(payload));
  },

  async redeemPoints(params: MembershipPointsRedeemInput): Promise<MembershipLedgerEntry> {
    const payload = await request<any>(`/v1/admin/memberships/${encodeURIComponent(params.membershipUuid)}/points/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        points: params.points,
        sourceRef: requiredSourceRef(params.sourceRef, params.idempotencyKey),
        idempotencyKey: params.idempotencyKey
      })
    });
    return mapLedgerEntry(unwrap<any>(payload));
  },

  async getRecentActivity(limit = 20): Promise<MembershipAuditEventRow[]> {
    const payload = await request<any>(`/v1/admin/memberships/activity/recent?limit=${limit}`, {
      method: 'GET'
    });
    const rows = unwrap<any[]>(payload);
    return (Array.isArray(rows) ? rows : []).map((r: any) => ({
      membershipUuid: asText(r?.membershipUuid),
      userUuid: asText(r?.userUuid),
      customerName: asText(r?.customerName),
      tierName: asText(r?.tierName),
      action: asText(r?.action),
      actorType: asText(r?.actorType),
      createdAt: asText(r?.createdAt)
    }));
  },

  async getExpiringMembers(withinDays = 30): Promise<ExpiringMemberRow[]> {
    const payload = await request<any>(`/v1/admin/memberships/members/expiring?withinDays=${withinDays}`, {
      method: 'GET'
    });
    const rows = unwrap<any[]>(payload);
    return (Array.isArray(rows) ? rows : []).map((r: any) => ({
      membershipUuid: asText(r?.membershipUuid),
      userUuid: asText(r?.userUuid),
      customerName: asText(r?.customerName),
      pointsAtRisk: asNumber(r?.pointsAtRisk),
      expiresAt: asText(r?.expiresAt)
    }));
  },

  async getUpgradeCandidates(thresholdPct = 80): Promise<UpgradeCandidateRow[]> {
    const payload = await request<any>(
      `/v1/admin/memberships/members/upgrade-candidates?thresholdPct=${thresholdPct}`,
      { method: 'GET' }
    );
    const rows = unwrap<any[]>(payload);
    return (Array.isArray(rows) ? rows : []).map((r: any) => ({
      membershipUuid: asText(r?.membershipUuid),
      userUuid: asText(r?.userUuid),
      customerName: asText(r?.customerName),
      currentTierName: asText(r?.currentTierName),
      nextTierName: asText(r?.nextTierName),
      currentTierLevel: asNumber(r?.currentTierLevel),
      progressValue: asNumber(r?.progressValue),
      progressUnit: asText(r?.progressUnit),
      progressPercent: asNumber(r?.progressPercent)
    }));
  },

  async previewBroadcast(audience: string): Promise<{ recipientCount: number; sampleRecipientNames: string[] }> {
    const payload = await request<any>('/v1/admin/memberships/broadcast/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audience, message: '__preview__' })
    });
    const data = unwrap<any>(payload);
    return {
      recipientCount: asNumber(data?.recipientCount),
      sampleRecipientNames: Array.isArray(data?.sampleRecipientNames)
        ? data.sampleRecipientNames.map((n: unknown) => asText(n))
        : [],
    };
  },

  async broadcast(params: MembershipBroadcastInput & { channels: string[] }): Promise<MembershipBroadcastResponse> {
    const payload = await request<any>('/v1/admin/memberships/broadcast', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        audience: params.audience,
        message: params.message,
        channels: params.channels,
      })
    });
    const data = unwrap<any>(payload);
    return { queued: asNumber(data?.queued) };
  }
};
